import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { invalidateDomain } from '@/api/hooks'

/** Map SSE change topics to action domains whose cached reads must be refreshed. */
export const TOPIC_DOMAINS: Record<string, string[]> = {
  tasks: ['tasks'],
  journey: ['journeys', 'schedule', 'labs', 'assistant'],
  inbox: ['inbox', 'notifications', 'clientapp'],
  sync: ['sync', 'connectors', 'system', 'jobs'],
  notifications: ['notifications'],
}

export type LiveState = 'connecting' | 'open' | 'closed'

/**
 * Subscribe to the server change feed (GET /v1/events). Each ping invalidates the
 * matching domains so open screens refresh without polling.
 */
export function useLiveUpdates(topics: string[] = Object.keys(TOPIC_DOMAINS), enabled = true): LiveState {
  const qc = useQueryClient()
  const [state, setState] = useState<LiveState>('connecting')
  const key = topics.join(',')
  useEffect(() => {
    if (!enabled || typeof EventSource === 'undefined') return
    let es: EventSource | null = null
    let retry: ReturnType<typeof setTimeout> | undefined
    let attempt = 0
    let closed = false
    const pending = new Set<string>()
    let flush: ReturnType<typeof setTimeout> | undefined

    const connect = () => {
      setState('connecting')
      es = new EventSource(`/v1/events?topics=${encodeURIComponent(key)}`, { withCredentials: true })
      es.onopen = () => {
        attempt = 0
        setState('open')
      }
      es.addEventListener('change', (ev) => {
        try {
          const { topic } = JSON.parse((ev as MessageEvent<string>).data) as { topic: string }
          for (const d of TOPIC_DOMAINS[topic] ?? [topic]) pending.add(d)
          // Coalesce bursts of pings into one refetch.
          clearTimeout(flush)
          flush = setTimeout(() => {
            void invalidateDomain(qc, ...pending)
            pending.clear()
          }, 250)
        } catch {
          /* ignore malformed */
        }
      })
      es.onerror = () => {
        es?.close()
        setState('closed')
        if (closed) return
        attempt++
        retry = setTimeout(connect, Math.min(30_000, 1000 * 2 ** attempt))
      }
    }
    connect()
    return () => {
      closed = true
      clearTimeout(retry)
      clearTimeout(flush)
      es?.close()
    }
  }, [key, enabled, qc])
  return state
}
